import { createApi } from '@reduxjs/toolkit/query/react';

import type { Role } from '@app/utils/protected-routes.utils';
import { setUser } from '@features/authentication/store/authentication.slice';
import { fetchBaseQueryWithTokenCheck } from '@shared/utils/api.utils';

import type { User } from './user-management.types';

type CreateUserWithResetPwLinkBody = Omit<User, 'creationDate' | 'lastModifiedDate' | 'locked'>;

interface CreateUserMutationBody extends Omit<User, 'creationDate' | 'lastModifiedDate' | 'locked'> {
  password: string;
}

interface EditUserMutationBody extends Omit<CreateUserMutationBody, 'password'> {
  username: string;
}

enum Endpoint {
  USERS = 'Users',
}

export const userManagementApiSlice = createApi({
  reducerPath: 'userManagementApi',
  baseQuery: fetchBaseQueryWithTokenCheck,
  tagTypes: [Endpoint.USERS],
  endpoints: (builder) => {
    return {
      getUsers: builder.query<User[], void>({
        query: () => '/api/v1/user',
        providesTags: [Endpoint.USERS],
      }),
      getRoles: builder.query<Role[], void>({
        query: () => '/api/v1/roles',
      }),
      getUserRoles: builder.query<Role[], string>({
        query: (email) => `/api/v1/user/${email}/roles`,
      }),
      getUserByEmail: builder.query<User, string>({
        query: (email) => `/api/v1/user/${email}`,
        async onQueryStarted(_, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            dispatch(setUser(data));
          } catch (err) {
            console.error('Sync failed', err);
          }
        },
      }),
      createNewUser: builder.mutation<User, CreateUserMutationBody>({
        query: (body) => ({
          url: '/api/v1/user',
          method: 'POST',
          body,
        }),
        invalidatesTags: [Endpoint.USERS],
      }),
      createUserWithResetPwLink: builder.mutation<User, CreateUserWithResetPwLinkBody>({
        query: (body) => ({
          url: '/api/v1/user/create-with-reset-password',
          method: 'POST',
          body,
        }),
        invalidatesTags: [Endpoint.USERS],
      }),
      editUser: builder.mutation<User, { email: string; body: EditUserMutationBody }>({
        query: ({ email, body }) => ({
          url: `/api/v1/user/${email}`,
          method: 'PUT',
          body,
        }),
        invalidatesTags: [Endpoint.USERS],
      }),
      deleteUser: builder.mutation({
        query: (email) => ({
          url: `/api/v1/user/${email}/tenant`,
          method: 'DELETE',
        }),
        invalidatesTags: [Endpoint.USERS],
      }),
      lockUser: builder.mutation({
        query: (email) => ({
          url: `/api/v1/user/${email}/lock`,
          method: 'PUT',
        }),
        invalidatesTags: [Endpoint.USERS],
      }),
      unlockUser: builder.mutation({
        query: (email) => ({
          url: `/api/v1/user/${email}/unlock`,
          method: 'PUT',
        }),
        invalidatesTags: [Endpoint.USERS],
      }),
    };
  },
});

export const {
  useGetUsersQuery,
  useGetRolesQuery,
  useGetUserRolesQuery,
  useGetUserByEmailQuery,
  useCreateNewUserMutation,
  useCreateUserWithResetPwLinkMutation,
  useEditUserMutation,
  useDeleteUserMutation,
  useLockUserMutation,
  useUnlockUserMutation,
} = userManagementApiSlice;
